import { useEffect, useState } from "react";

import {
  cloneLayoutSettings,
  defaultLayoutSettings,
  isLayoutPreviewMessage,
  layoutPresetChangeEvent,
  readLayoutPresetStore,
  resolveLayoutForViewport,
  type LayoutSettings,
} from "./layout-presets";

type LayoutViewport = {
  height: number;
  width: number;
};

function getLayoutViewport(): LayoutViewport {
  const isTouchViewport = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  if (isTouchViewport && screenWidth > 0 && screenHeight > 0) {
    const isPortrait = window.matchMedia("(orientation: portrait)").matches;
    const shortSide = Math.min(screenWidth, screenHeight);
    const longSide = Math.max(screenWidth, screenHeight);

    return isPortrait
      ? { height: longSide, width: shortSide }
      : { height: shortSide, width: longSide };
  }

  return {
    height: window.innerHeight,
    width: window.innerWidth,
  };
}

function getSavedLayoutForViewport() {
  if (typeof window === "undefined") {
    return defaultLayoutSettings;
  }

  const viewport = getLayoutViewport();

  return resolveLayoutForViewport(readLayoutPresetStore(), viewport.width, viewport.height);
}

export function useLayoutSettings(isLayoutPreview: boolean) {
  const [layout, setLayout] = useState<LayoutSettings>(getSavedLayoutForViewport);

  useEffect(() => {
    const refreshSavedLayout = () => {
      if (!isLayoutPreview) {
        setLayout(getSavedLayoutForViewport());
      }
    };

    const handlePreviewMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin || !isLayoutPreviewMessage(event.data)) {
        return;
      }

      setLayout(cloneLayoutSettings(event.data.layout));
    };

    window.addEventListener("message", handlePreviewMessage);
    window.addEventListener("resize", refreshSavedLayout);
    window.addEventListener("storage", refreshSavedLayout);
    window.addEventListener(layoutPresetChangeEvent, refreshSavedLayout);

    return () => {
      window.removeEventListener("message", handlePreviewMessage);
      window.removeEventListener("resize", refreshSavedLayout);
      window.removeEventListener("storage", refreshSavedLayout);
      window.removeEventListener(layoutPresetChangeEvent, refreshSavedLayout);
    };
  }, [isLayoutPreview]);

  return layout;
}
